import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export const EVENT_LOG_PAGE_SIZE = 25;

const BOTTOM_THRESHOLD_PX = 8;
const TOP_THRESHOLD_PX = 4;

export default function useEventLog({
  isOpen,
  messages,
  pageSize = EVENT_LOG_PAGE_SIZE,
}) {
  const items = messages ?? [];
  const messageCount = items.length;
  const logRef = useRef(null);
  const autoScrollRef = useRef(true);
  const pendingPrependRef = useRef(null);
  const previousMessageCountRef = useRef(messageCount);
  const wasOpenRef = useRef(false);
  const [visibleCount, setVisibleCount] = useState(() => Math.min(pageSize, messageCount));

  const pendingMessageCount = Math.max(0, messageCount - previousMessageCountRef.current);
  const displayedCount = Math.min(
    messageCount,
    autoScrollRef.current ? visibleCount : visibleCount + pendingMessageCount,
  );
  const entries = items.slice(Math.max(0, messageCount - displayedCount));

  useLayoutEffect(() => {
    const previousMessageCount = previousMessageCountRef.current;
    previousMessageCountRef.current = messageCount;

    setVisibleCount((current) => {
      if (messageCount < previousMessageCount) {
        autoScrollRef.current = true;
        return Math.min(pageSize, messageCount);
      }

      const addedCount = messageCount - previousMessageCount;
      const nextCount = autoScrollRef.current
        ? Math.min(pageSize, messageCount)
        : Math.min(messageCount, current + addedCount);
      return current === nextCount ? current : nextCount;
    });
  }, [messageCount, pageSize]);

  useLayoutEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened) return;

    autoScrollRef.current = true;
    pendingPrependRef.current = null;
    setVisibleCount(Math.min(pageSize, messageCount));
  }, [isOpen, messageCount, pageSize]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = logRef.current;
    if (!el) return;

    if (pendingPrependRef.current) {
      const { scrollHeight, scrollTop } = pendingPrependRef.current;
      el.scrollTop = scrollTop + (el.scrollHeight - scrollHeight);
      pendingPrependRef.current = null;
      return;
    }

    if (autoScrollRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [displayedCount, isOpen, messageCount]);

  const handleLogScroll = useCallback(() => {
    const el = logRef.current;
    if (!el) return;

    if (
      el.scrollTop <= TOP_THRESHOLD_PX
      && visibleCount < messageCount
      && !pendingPrependRef.current
    ) {
      autoScrollRef.current = false;
      pendingPrependRef.current = {
        scrollHeight: el.scrollHeight,
        scrollTop: el.scrollTop,
      };
      setVisibleCount(current => Math.min(messageCount, current + pageSize));
      return;
    }

    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isAtNewestEntry = distance <= BOTTOM_THRESHOLD_PX;
    autoScrollRef.current = isAtNewestEntry;
    if (isAtNewestEntry) {
      setVisibleCount(current => (
        current > pageSize ? Math.min(pageSize, messageCount) : current
      ));
    }
  }, [messageCount, pageSize, visibleCount]);

  return {
    entries,
    handleLogScroll,
    logRef,
    startIndex: messageCount - displayedCount,
  };
}
